import { useState } from 'react';
import api from '../lib/api';
import { Avatar, PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';

const arr = (r) => (Array.isArray(r) ? r : (r?.users || r?.items || r?.content || []));

export default function AdminMembersPage() {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const { loading, error, data, reload } = useAsync(
    () => api.listAdminUsers({ q: query || undefined, limit: 200 }).then(arr),
    [query]
  );

  const del = async (u) => {
    if (!window.confirm(`Permanently delete ${u.username || u.email}? This cannot be undone.`)) return;
    const blacklist = window.confirm(
      `Also blacklist ${u.email}?\n\nOK = blacklist (this email can never create an account again).\nCancel = delete only.`
    );
    try { await api.adminDeleteUser(u.id, { blacklist }); reload(); }
    catch (e) { alert(e.message); }
  };

  // design/036 — suspended people can still sign in, but only see the reason
  // and Sign out / Delete account. Their web subscription stops renewing.
  const suspend = async (u) => {
    const reason = window.prompt(
      `Suspend ${u.username || u.email}?\n\nThey'll see this reason when they sign in (optional):`, '');
    if (reason === null) return;
    try { await api.adminSuspendUser(u.id, reason.trim()); reload(); }
    catch (e) { alert(e?.data?.message || e.message); }
  };
  const unsuspend = async (u) => {
    if (!window.confirm(`Unsuspend ${u.username || u.email}? They'll get full access again.`)) return;
    try { await api.adminUnsuspendUser(u.id); reload(); }
    catch (e) { alert(e?.data?.message || e.message); }
  };

  const setEventLeader = async (u, eventLeader) => {
    try { await api.adminSetEventLeader(u.id, eventLeader); reload(); }
    catch (e) { alert(e.message); }
  };
  const setGroupLeader = async (u, groupLeader) => {
    try { await api.adminSetGroupLeader(u.id, groupLeader); reload(); }
    catch (e) { alert(e.message); }
  };
  const setPublisher = async (u, publisher) => {
    try { await api.adminSetPublisher(u.id, publisher); reload(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div className="page">
      <PageHead title="Members" sub="All platform users" />

      <form onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }} style={{ marginBottom: 14, display: 'flex', gap: 8, maxWidth: 480 }}>
        <input className="input" placeholder="Search by name, username, or email" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn">Search</button>
        {query && <button type="button" className="btn" onClick={() => { setQ(''); setQuery(''); }}>Clear</button>}
      </form>

      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <Section style={{ padding: 0 }}>
          {data.length === 0 ? <div style={{ padding: 18 }}><Empty>No users found.</Empty></div> : (
            <table className="tbl">
              <thead><tr><th>Member</th><th>Email</th><th>Role</th><th></th></tr></thead>
              <tbody>
                {data.map((u) => {
                  const name = [u.firstname, u.lastname].filter(Boolean).join(' ') || u.username;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={u.username || u.email} size={28} />
                          <div>
                            <div style={{ fontWeight: 600 }}>{name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>@{u.username}</div>
                            {u.suspended && (
                              <div style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: 2 }}>
                                Suspended{u.suspendedReason ? ` — ${u.suspendedReason}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {u.isAdmin
                          ? <span className="pill" style={{ background: 'var(--amber-bg)', color: 'var(--amber-text)' }}>Super</span>
                          : u.publisher
                            ? <span className="pill" style={{ background: 'var(--wpl-100)', color: 'var(--wpl-700)' }}>Publisher</span>
                            : (u.eventLeader || u.groupLeader)
                              ? <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                                  {u.eventLeader && <span className="pill" style={{ background: '#dbeafe', color: '#1e40af' }}>Event Leader</span>}
                                  {u.groupLeader && <span className="pill" style={{ background: 'var(--green-bg, #dcfce7)', color: 'var(--green-text, #166534)' }}>Group Leader</span>}
                                </span>
                              : <span className="pill member">Member</span>}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!u.isAdmin && (u.publisher
                          ? <button className="btn btn-sm" onClick={() => setPublisher(u, false)}>Remove publisher</button>
                          : <button className="btn btn-sm" onClick={() => setPublisher(u, true)}>Make publisher</button>)}
                        {!u.isAdmin && !u.publisher && (u.eventLeader
                          ? <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => setEventLeader(u, false)}>Remove event leader</button>
                          : <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => setEventLeader(u, true)}>Make event leader</button>)}
                        {!u.isAdmin && !u.publisher && (u.groupLeader
                          ? <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => setGroupLeader(u, false)}>Remove group leader</button>
                          : <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => setGroupLeader(u, true)}>Make group leader</button>)}
                        {!u.isAdmin && (u.suspended
                          ? <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => unsuspend(u)}>Unsuspend</button>
                          : <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => suspend(u)}>Suspend</button>)}
                        {!u.isAdmin && <button className="btn btn-sm btn-danger" style={{ marginLeft: 6 }} onClick={() => del(u)}>Delete</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <StaffForm
          title="Add a content publisher"
          role="PUBLISHER"
          cta="Add publisher"
          blurb="Publishers create and manage courses, practices and groups."
          onDone={reload} />
        <StaffForm
          title="Add an Event leader"
          role="EVENT_LEADER"
          cta="Add event leader"
          blurb="Event leaders create and run events."
          onDone={reload} />
        <StaffForm
          title="Add a Group leader"
          role="GROUP_LEADER"
          cta="Add group leader"
          blurb="Group leaders create and run groups."
          onDone={reload} />
      </div>
    </div>
  );
}

/**
 * design/027 — the superadmin adds staff. Creates the account (temporary
 * password emailed; they set their own on first sign-in), or gives the role
 * to an existing account with that email. Staff never pay the subscription.
 */
function StaffForm({ title, role, cta, blurb, onDone }) {
  const [f, setF] = useState({ email: '', firstname: '', lastname: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) return;
    setBusy(true);
    try {
      const r = await api.adminCreateStaff({ ...f, email: f.email.trim(), role });
      alert(r?.created
        ? `Account created for ${r.email}. They've been emailed a temporary password.`
        : `${r?.email || f.email} already had an account — the role has been added.`);
      setF({ email: '', firstname: '', lastname: '' });
      onDone?.();
    } catch (err) { alert(err?.data?.message || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Section title={title} style={{ flex: '1 1 320px', maxWidth: 480 }}>
      <p style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginTop: 0, marginBottom: 10 }}>
        {blurb} They get full access with no subscription. We'll email them a temporary password.
      </p>
      <form onSubmit={submit}>
        <div className="form-field"><label>Email <span className="req">*</span></label>
          <input className="input" type="email" placeholder="name@example.com" value={f.email} onChange={set('email')} /></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="form-field" style={{ flex: 1 }}><label>First name</label>
            <input className="input" value={f.firstname} onChange={set('firstname')} /></div>
          <div className="form-field" style={{ flex: 1 }}><label>Last name</label>
            <input className="input" value={f.lastname} onChange={set('lastname')} /></div>
        </div>
        <button className="btn btn-primary" disabled={busy || !f.email.trim()}>{busy ? 'Adding…' : cta}</button>
      </form>
    </Section>
  );
}
